import { useContext, useEffect } from 'react';
import { AuthContext } from '@/context/auth-context';
import { useOrgStore } from '@/stores/org-store';

declare global {
  interface Window {
    Canny?: {
      (action: string, options?: Record<string, unknown>): void;
      q?: Array<[string, Record<string, unknown>]>;
    };
  }
}

/**
 * Loads the Canny SDK and identifies the current user, so `data-canny-link`
 * anchors carry the session straight into the portal.
 *
 * Pass `enabled` false where the portal is unreachable — the SDK has to finish
 * identifying before any such anchor is clickable, so it cannot be deferred
 * until the click itself.
 */
export function useCannyIdentify(enabled = true) {
  const { user } = useContext(AuthContext);
  const { currentOrg } = useOrgStore();
  const cannyAppId = import.meta.env.VITE_CANNY_APP_ID;

  useEffect(() => {
    if (!enabled || !user || !cannyAppId) {
      return;
    }

    const identifyUser = () => {
      if (!window.Canny) {
        return;
      }

      const username =
        `${user.firstName} ${user.lastName}`.trim() || user.email;
      window.Canny('identify', {
        appID: cannyAppId,
        user: {
          email: user.email,
          name: username !== 'Guest Guest' ? username : user.email,
          id: user.id,
          avatarURL: user.image || undefined,
          companies: currentOrg
            ? [{ id: currentOrg.orgid, name: currentOrg.name }]
            : [],
        },
      });
    };

    if (window.Canny) {
      identifyUser();
      return;
    }

    if (!document.getElementById('canny-jssdk')) {
      const script = document.createElement('script');
      script.id = 'canny-jssdk';
      script.async = true;
      script.src = 'https://canny.io/sdk.js';
      script.onload = identifyUser;
      document.head.appendChild(script);
    }
  }, [enabled, user, cannyAppId, currentOrg]);
}
